"use client";

import { useState, useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaArrowRight, FaCalendarAlt } from "react-icons/fa";
import axios from "../../../../components/axios";
import HTMLParser from "../../../../components/Parser";

const StoriesSection = () => {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const { data } = await axios.get("stories/lateststories");
        setStories(data);
      } catch (error) {
        console.error("Error fetching stories:", error);
        // Fallback data if API fails
      }
    };

    fetchStories();
  }, []);

  return (
    <StoriesContainer>
      <ContentWrapper>
        <TextContainer>
          <SectionTitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0 }}
          >
            Heart Warming Stories
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Explore The Vision And Mission Of KRO
          </SectionSubtitle>
        </TextContainer>

        <StoriesGrid>
          {stories.map((story, index) => (
            <StoryCard
              key={story.id}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
            >
              <CardImageContainer>
                <CardImage src={story.image} alt={story.title} />
                <ImageGradient />
              </CardImageContainer>

              <CardContent>
                <DateContainer>
                  <FaCalendarAlt />
                  <StoryDate>{story.published_date}</StoryDate>
                </DateContainer>

                <StoryTitle>{story.title}</StoryTitle>
                <StoryDescription>
                  {" "}
                  <HTMLParser html={story.content} type="card" />
                </StoryDescription>

                <LearnMoreContainer>
                  <LearnMoreButton>
                    <span>Learn More</span>
                    <ArrowIcon>
                      <FaArrowRight />
                    </ArrowIcon>
                  </LearnMoreButton>
                </LearnMoreContainer>
              </CardContent>
            </StoryCard>
          ))}
        </StoriesGrid>
      </ContentWrapper>
    </StoriesContainer>
  );
};

export default StoriesSection;

// Styled Components
const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
`;

const ImageGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const ArrowIcon = styled.div`
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
`;

const LearnMoreButton = styled.button`
  background: none;
  border: none;
  color: #10b981;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    color: #059669;

    ${ArrowIcon} {
      transform: translateX(4px);
    }
  }
`;

const StoryCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(226, 232, 240, 0.8);

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-8px);
    border-color: rgba(16, 185, 129, 0.2);

    ${CardImage} {
      transform: scale(1.05);
    }

    ${ImageGradient} {
      opacity: 1;
    }
  }
`;

const StoriesContainer = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 5rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const TextContainer = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  color: #10b981;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const StoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CardImageContainer = styled.div`
  height: 240px;
  overflow: hidden;
  position: relative;
`;

const CardContent = styled.div`
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 500;

  svg {
    color: #10b981;
    font-size: 0.8rem;
  }
`;

const StoryDate = styled.span`
  color: #64748b;
`;

const StoryTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.4;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const StoryDescription = styled.p`
  color: #64748b;
  line-height: 1.7;
  margin: 0;
  font-size: 0.8rem;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LearnMoreContainer = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
`;
